// Top-level signed-in shell.
//
// Layout:
//   ┌───────────────────────┬───────────────────────────────┐
//   │ ProfileSwitcher       │                               │
//   ├───────────────────────┤                               │
//   │ ChatListPane          │   ProfileDetail               │
//   │   (live telegram      │   (cards: MT5 / Backend /     │
//   │    channels list)     │    Telegram login / Trading   │
//   │                       │    Channels (per-chat) /      │
//   │                       │    Trade Audit / Playground / │
//   │                       │    Logs)                      │
//   └───────────────────────┴───────────────────────────────┘
//
// State owned here:
//   - profiles snapshot + active profile (persisted in localStorage)
//   - per-profile selected chat (persisted in localStorage)
//   - live telegramSessionState for the active profile
//   - in-flight tradingChannels[] mirror so the sidebar's "configured" badge
//     and the right-pane card stay in sync without two HTTP fetches
import { useCallback, useEffect, useState } from "react";
import type {
	DesktopProfileSummary,
	DesktopProfilesSnapshot,
} from "@shared/api/schemas/profiles";
import type {
	TelegramChannelSummary,
	TelegramSessionState,
} from "@shared/api/schemas/telegram";
import type { TradingChannelConfig } from "@shared/api/schemas/trading-channels";
import { ApiError, httpClient } from "../../client/http-client";
import type { StoredSession } from "../../client/jwt-store";
import { wsClient } from "../../client/ws-client";
import { useLiveResource } from "../../hooks/useLiveResource";
import type { ThemeMode } from "../../lib/theme";
import { UserManagementModal } from "../admin/UserManagementModal";
import { ChatListPane } from "./ChatListPane";
import { ProfileDetail } from "./ProfileDetail";
import { ProfileSwitcher } from "./ProfileSwitcher";

const SELECTED_PROFILE_KEY = "signallens.selectedProfileId";
const SELECTED_CHAT_KEY_PREFIX = "signallens.selectedChatId.";

const readSelectedChatId = (profileId: string | null): number | null => {
	if (!profileId) return null;
	const raw = localStorage.getItem(SELECTED_CHAT_KEY_PREFIX + profileId);
	if (!raw) return null;
	const parsed = Number.parseInt(raw, 10);
	return Number.isFinite(parsed) ? parsed : null;
};

const writeSelectedChatId = (profileId: string, chatId: number | null) => {
	const key = SELECTED_CHAT_KEY_PREFIX + profileId;
	if (chatId === null) {
		localStorage.removeItem(key);
	} else {
		localStorage.setItem(key, String(chatId));
	}
};

interface WorkspacePageProps {
	session: StoredSession;
	themeMode: ThemeMode;
	onToggleTheme: () => void;
	onLoggedOut: () => void;
}

export const WorkspacePage = ({
	session,
	themeMode,
	onToggleTheme,
	onLoggedOut,
}: WorkspacePageProps) => {
	const [snapshot, setSnapshot] = useState<DesktopProfilesSnapshot | null>(
		null,
	);
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
		() => localStorage.getItem(SELECTED_PROFILE_KEY),
	);
	const [selectedChatId, setSelectedChatId] = useState<number | null>(() =>
		readSelectedChatId(localStorage.getItem(SELECTED_PROFILE_KEY)),
	);
	const [telegramChannels, setTelegramChannels] = useState<
		TelegramChannelSummary[]
	>([]);
	const [telegramReady, setTelegramReady] = useState(false);
	const [tradingChannels, setTradingChannels] = useState<
		TradingChannelConfig[]
	>([]);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [isUserManagementOpen, setUserManagementOpen] = useState(false);

	const refreshProfiles = useCallback(() => {
		setLoadError(null);
		httpClient.profiles
			.list()
			.then((next) => {
				setSnapshot(next);
				setLoading(false);
			})
			.catch((err) => {
				if (err instanceof ApiError && err.status === 401) {
					onLoggedOut();
					return;
				}
				setLoadError(err instanceof ApiError ? err.message : String(err));
				setLoading(false);
			});
	}, [onLoggedOut]);

	useEffect(() => {
		wsClient.start();
		refreshProfiles();
		const unsubscribe = wsClient.onProfilesChanged((next) => {
			setSnapshot(next);
		});
		return () => {
			unsubscribe();
		};
	}, [refreshProfiles]);

	// Persist active profile.
	useEffect(() => {
		if (selectedProfileId) {
			localStorage.setItem(SELECTED_PROFILE_KEY, selectedProfileId);
		} else {
			localStorage.removeItem(SELECTED_PROFILE_KEY);
		}
	}, [selectedProfileId]);

	// Persist per-profile selected chat.
	useEffect(() => {
		if (!selectedProfileId) return;
		writeSelectedChatId(selectedProfileId, selectedChatId);
	}, [selectedProfileId, selectedChatId]);

	// When the active profile changes, hydrate selectedChatId from storage.
	useEffect(() => {
		setSelectedChatId(readSelectedChatId(selectedProfileId));
		// Reset live data; new profile's WS replay will repopulate.
		setTelegramChannels([]);
		setTelegramReady(false);
		setTradingChannels([]);
	}, [selectedProfileId]);

	// Auto-select the first profile if none is selected or if the selected one
	// was deleted/lost ownership.
	useEffect(() => {
		if (!snapshot) return;
		if (snapshot.profiles.length === 0) {
			setSelectedProfileId(null);
			return;
		}
		const stillPresent = snapshot.profiles.some(
			(p) => p.id === selectedProfileId,
		);
		if (!stillPresent) {
			setSelectedProfileId(snapshot.profiles[0].id);
		}
	}, [snapshot, selectedProfileId]);

	// Settings hydration uses useLiveResource (handles initial fetch,
	// re-fetch on backend → running, re-fetch on WS reconnect). The
	// hook reads `selectedProfileId` as its key so a profile switch
	// resets and refetches.
	const { value: settingsValue } = useLiveResource<TradingChannelConfig[]>({
		key: selectedProfileId,
		fetcher: () =>
			httpClient.settings
				.get(selectedProfileId as string)
				.then((settings) => settings.settings.tradingChannels ?? []),
		refetchOnBackendRunning: selectedProfileId
			? { profileId: selectedProfileId }
			: undefined,
		clearOnError: false,
	});
	useEffect(() => {
		if (settingsValue) setTradingChannels(settingsValue);
	}, [settingsValue]);

	// Telegram channel + ready-flag live updates (independent of the
	// settings fetch since they come from a different WS event).
	//
	// When the backend transitions OUT of running, the telegram session is
	// gone server-side. Clear our cached channels + ready flag so the
	// sidebar's chat list (ChatListPane) and any "connected" affordance
	// drop back to their empty/disconnected rendering. Without this clear,
	// stale entries linger in the SPA until the next profile-switch or
	// reload — exact same bug the TelegramLoginCard had.
	useEffect(() => {
		if (!selectedProfileId) return;
		const unsubscribeTelegram = wsClient.onTelegramChanged(
			(incomingProfileId, state: TelegramSessionState) => {
				if (incomingProfileId !== selectedProfileId) return;
				setTelegramChannels(state.channels);
				setTelegramReady(state.step === "ready");
			},
		);
		const unsubscribeBackend = wsClient.onBackendChanged(
			(incomingProfileId, backend) => {
				if (incomingProfileId !== selectedProfileId) return;
				if (backend.status !== "running") {
					setTelegramChannels([]);
					setTelegramReady(false);
				}
			},
		);
		const unsubscribeProfile = wsClient.subscribeProfile(selectedProfileId);
		return () => {
			unsubscribeTelegram();
			unsubscribeBackend();
			unsubscribeProfile();
		};
	}, [selectedProfileId]);

	const handleLogout = useCallback(async () => {
		wsClient.stop();
		await httpClient.auth.logout();
		onLoggedOut();
	}, [onLoggedOut]);

	const profiles: DesktopProfileSummary[] = snapshot?.profiles ?? [];
	const selectedProfile =
		profiles.find((p) => p.id === selectedProfileId) ?? null;

	return (
		<>
		<UserManagementModal
			isOpen={isUserManagementOpen}
			onClose={() => setUserManagementOpen(false)}
			currentUserId={session.user.id}
		/>
		<div className="grid h-full overflow-hidden grid-cols-[minmax(280px,24vw)_minmax(0,1fr)] gap-px bg-[color:var(--color-tg-line)]">
			<aside className="tg-sidebar grid h-full min-h-0 grid-rows-[auto_1fr] overflow-hidden">
				<ProfileSwitcher
					profiles={profiles}
					selectedProfileId={selectedProfileId}
					session={session}
					onSelect={setSelectedProfileId}
					onProfilesChanged={refreshProfiles}
					onLogout={() => void handleLogout()}
					onOpenUserManagement={
						session.user.role === "admin"
							? () => setUserManagementOpen(true)
							: undefined
					}
				/>

				<ChatListPane
					profileId={selectedProfileId}
					channels={telegramChannels}
					telegramReady={telegramReady}
					tradingChannels={tradingChannels}
					selectedChatId={selectedChatId}
					onSelectChat={setSelectedChatId}
				/>
			</aside>

			{selectedProfile ? (
				<ProfileDetail
					key={selectedProfile.id}
					profile={selectedProfile}
					themeMode={themeMode}
					telegramChannels={telegramChannels}
					telegramReady={telegramReady}
					tradingChannels={tradingChannels}
					selectedChatId={selectedChatId}
					isAdmin={session.user.role === "admin"}
					visibilityFlags={session.user.visibilityFlags ?? {}}
					onTradingChannelsChange={setTradingChannels}
					onToggleTheme={onToggleTheme}
					onProfilesChanged={refreshProfiles}
					onProfileDeleted={() => {
						setSelectedProfileId(null);
						refreshProfiles();
					}}
				/>
			) : (
				<main className="tg-pane grid h-full place-items-center px-6 py-5 text-center">
					<div className="max-w-md">
						<h2 className="tg-text-primary text-[clamp(1.4rem,2.1vw,1.8rem)] font-bold leading-tight">
							{loading
								? "Loading profiles..."
								: profiles.length === 0
									? "No profiles yet"
									: "Pick a profile"}
						</h2>
						{loadError ? (
							<div
								className="tg-alert-error mt-4 rounded-xl px-4 py-3 text-left"
								role="alert"
							>
								{loadError}
							</div>
						) : (
							<p className="tg-text-muted mt-3 text-[0.95rem] leading-7">
								{profiles.length === 0
									? "Open the profile menu in the top-left and choose “Create new profile” to get started."
									: "Open the profile menu in the top-left to switch profiles."}
							</p>
						)}
					</div>
				</main>
			)}
		</div>
		</>
	);
};
